import { ApiError, OfflineError } from "@/lib/api";
import { issueTitleOrPlaceholder } from "@/lib/issues";
import type { PendingAppend, PendingPhoto, PendingRecord } from "@/offline/db";
import { uploadImageFilename } from "@/lib/upload-filename";

/** Hard cap on retry backoff — roughly ten minutes between attempts. */
const MAX_BACKOFF_MS = 600_000;
/** Floor on retry backoff — never retry faster than ~1s. */
const MIN_BACKOFF_MS = 1_000;
/** Base of the exponential — see {@link outboxBackoff} for how it scales. */
const BASE_BACKOFF_MS = 1_000;
/** Jitter spread applied to each backoff value: ±20%. */
const JITTER_FRACTION = 0.2;

/**
 * Decides whether an outbox upload failure should be retried.
 *
 * The outbox model is *retry-forever* for transient failures, so this
 * deliberately errs toward retrying:
 *
 *  - `OfflineError` — no connectivity; will succeed once signal returns.
 *  - A `TypeError` — `fetch` rejects with `TypeError` for network-level
 *    failures (DNS, connection reset, transfer dropped mid-flight).
 *  - `ApiError` with status >= 500, 429, or 503 — server briefly
 *    unreachable, rate limited, or temporarily unavailable.
 *  - `ApiError` 401 — the stale-token race. The access token is fetched
 *    fresh per attempt but can expire between being attached and the server
 *    validating it (canonically: the app is backgrounded mid-drain and the
 *    queued request lands after the token aged out). The retry's fresh
 *    `getAccessToken()` succeeds; a genuinely revoked session keeps failing
 *    into the stuck-record alert instead of silently burning the capture.
 *  - An *unknown* error shape — classified transient-by-default. A capture
 *    must never be lost to a misclassified error; a stuck retry loop is far
 *    cheaper than a dropped record, and reconcile-on-boot keeps it correct.
 *
 * Only an `ApiError` the server *actively rejected* with a non-retryable 4xx
 * (400/403/404/409/413/422, etc.) is permanent — retrying cannot fix a
 * request the server refuses on its merits.
 */
export function isRetryable(error: unknown): boolean {
  if (error instanceof OfflineError) return true;
  // `fetch` surfaces network/transfer-level failures as a TypeError.
  if (error instanceof TypeError) return true;
  if (error instanceof ApiError) {
    const { status } = error;
    if (status === 401 || status === 429 || status === 503) return true;
    if (status >= 500) return true;
    // Any other 4xx is a permanent rejection.
    return false;
  }
  // Unknown error shape: transient-by-default — the model is retry-forever.
  return true;
}

/** Upper bound on a captured response body — context, not a payload dump. */
const MAX_CAPTURED_BODY_CHARS = 2_048;

/**
 * Sentry fingerprint for an outbox capture: `["outbox", phase, key]`, where
 * `key` is the HTTP status for an `ApiError`, the error's class name for any
 * other `Error`, and omitted when there is nothing meaningful to key on.
 *
 * Without an explicit fingerprint every `ApiError` groups by minified stack
 * frame: unrelated failures pile into one mixed issue while identical
 * failures split across several (OTUS-WEB-VITE-16 vs -1R/-1G). One issue per
 * failure mode — phase × status — is what makes the dashboard triagable.
 */
export function outboxFingerprint(phase: string, error?: unknown): string[] {
  if (error instanceof ApiError) return ["outbox", phase, String(error.status)];
  if (error instanceof Error) return ["outbox", phase, error.name];
  return ["outbox", phase];
}

/**
 * The server's response body, JSON-serialized and bounded, for an outbox
 * Sentry capture — or `undefined` when there is none to report.
 *
 * The body is the server's own rejection reason (a DRF `detail` / field-error
 * payload — diagnostic text, never user content), and it is the difference
 * between a triagable capture and a guessing game: a bare "API error 400"
 * says nothing about *why* the server refused a queued photo.
 */
export function apiErrorBodyForCapture(error: unknown): string | undefined {
  if (!(error instanceof ApiError) || error.body == null) return undefined;
  try {
    const serialized = JSON.stringify(error.body);
    return serialized && serialized.length > MAX_CAPTURED_BODY_CHARS
      ? serialized.slice(0, MAX_CAPTURED_BODY_CHARS)
      : serialized;
  } catch {
    // A non-serializable body (cycles, BigInt) is context lost, not an error.
    return undefined;
  }
}

/**
 * Retry delay in milliseconds for a given `failureCount`.
 *
 * This is wired into TanStack Query's mutation `retryDelay`, which TanStack
 * calls with a **1-indexed `failureCount`** — `1` on the first retry, `2` on
 * the second, and so on (NOT a 0-indexed attempt). So the first retry waits
 * `1000 * 2 ** 1 = 2000ms` (~2s before jitter), the second ~4s, the third
 * ~8s, doubling each time. That is the intended schedule: a quick first
 * retry for a momentary blip, then backing off fast.
 *
 * Growth is capped at ~10 minutes, with ±20% jitter to spread out a
 * thundering herd of queued records all retrying at once. Never returns
 * below ~1s. A non-finite or non-positive input is floored to `0`
 * (yielding the base delay) so a bad caller can't break the schedule.
 */
export function outboxBackoff(failureCount: number): number {
  const safeAttempt =
    Number.isFinite(failureCount) && failureCount > 0 ? failureCount : 0;
  const exponential = BASE_BACKOFF_MS * 2 ** safeAttempt;
  const capped = Math.min(exponential, MAX_BACKOFF_MS);
  const jitter = capped * JITTER_FRACTION * (Math.random() * 2 - 1);
  return Math.max(MIN_BACKOFF_MS, Math.round(capped + jitter));
}

/** True iff the entry is a multi-photo record (drains per-photo via v2.0). */
export function isMultiPhotoRecord(entry: PendingRecord): boolean {
  return Array.isArray(entry.photos) && entry.photos.length > 0;
}

/**
 * Assembles the v2.0 multipart body for ONE photo of a multi-photo create
 * (`POST /projects/2.0/projects/{id}/records/`). The record's `clientUuid`
 * rides as `client_record_uuid` so the backend get-or-creates one logical
 * record and groups every photo under it; the photo's own `photoId`
 * (`client_photo_uuid`) is the per-photo idempotency key (sent as the
 * `X-Idempotency-Key` header by the drain).
 *
 * Unlike the add-only append (`buildAppendFormData`), a create carries the
 * record-level fields (`plan` / `pin` / `tags` / `metadata`) on EVERY photo —
 * the backend applies them only on the request that actually creates the
 * record (get-or-create by `client_record_uuid`) and ignores them on the
 * appends, so sending them on all photos is safe regardless of which one lands
 * first. The per-photo fields (`image` / `original` / `annotations` /
 * `taken_at_hint`) match the fields the v2.0 create path sends.
 */
export function buildMultiPhotoFormData(
  entry: PendingRecord,
  photo: PendingPhoto,
): FormData {
  const formData = new FormData();

  formData.append("client_record_uuid", entry.clientUuid);

  formData.append("image", photo.annotatedBlob, "capture.jpg");
  formData.append(
    "original",
    photo.originalBlob,
    uploadImageFilename(photo.originalFileName, photo.originalBlob.type),
  );

  if (photo.takenAtHint) {
    formData.append("taken_at_hint", photo.takenAtHint);
    formData.append("taken_at_source_hint", "file_last_modified");
  }

  const { annotations } = photo;
  const hasDrawings =
    Array.isArray(annotations.drawings) && annotations.drawings.length > 0;
  const hasCrop = annotations.crop != null;
  const hasDownscale = annotations.downscale_max_px != null;
  // A rotation with no drawings/crop is still meaningful: it is what puts the
  // photo back the right way up when the record is re-edited.
  const hasRotation = !!annotations.rotation;
  if (hasDrawings || hasCrop || hasDownscale || hasRotation) {
    formData.append(
      "annotations",
      JSON.stringify({
        drawings: annotations.drawings,
        crop: annotations.crop,
        rotation: annotations.rotation,
        downscaleMaxPx: annotations.downscale_max_px,
      }),
    );
  }

  if (entry.planId) formData.append("plan", entry.planId);
  if (entry.pinX != null) formData.append("pin_x", String(entry.pinX));
  if (entry.pinY != null) formData.append("pin_y", String(entry.pinY));
  if (entry.pinBearing != null)
    formData.append("pin_bearing", String(entry.pinBearing));

  if (entry.tags.length > 0) {
    formData.append("tags", JSON.stringify(entry.tags));
  }

  // The issue block rides EVERY photo of the batch, not just the first.
  // The drain fires the photos in parallel under one `client_record_uuid` and
  // the backend raises the issue only on whichever request wins `created` —
  // so attaching it to one chosen photo would silently drop the issue whenever
  // a different one won the race. Attaching it to all is safe: the losers see
  // `created = false` and skip the block, exactly as they already do for tags.
  if (entry.issue) {
    // Last-ditch title guard. `title` is required server-side, and the block
    // rides the record's OWN create — so a blank title comes back a
    // non-retryable 400 that fails the whole entry and strands the photo, not
    // just the issue. Resolved here rather than only where the draft is built
    // so an entry ALREADY queued blank (by a build without the guard) is
    // rescued on its next retry instead of failing forever.
    formData.append("issue_title", issueTitleOrPlaceholder(entry.issue.title));
    formData.append("issue_client_uuid", entry.issue.clientUuid);
    if (entry.issue.description) {
      formData.append("issue_description", entry.issue.description);
    }
    if (entry.issue.assignedToId) {
      formData.append("issue_assigned_to", entry.issue.assignedToId);
    }
    if (entry.issue.dueDate) {
      formData.append("issue_due_date", entry.issue.dueDate);
    }
  }

  if (Object.keys(entry.metadata).length > 0) {
    formData.append("metadata", JSON.stringify(entry.metadata));
  }

  return formData;
}

/**
 * Assembles the multipart body for a follow-up MARKUP `PATCH` of a queued
 * record that was re-edited while its create upload was in flight.
 *
 * Mirrors the `patchRecordMarkups` synced-path `FormData` in
 * `use-submit-record.ts`: the annotated `image`, the `original` bytes, and
 * the `annotations` payload. The drain fires this — alongside the metadata
 * `PATCH` — whenever the entry's `revision` advanced past the uploaded
 * snapshot, so a markup re-edit is never silently lost (see
 * [[offline-record-outbox]] H2).
 */
export function buildMarkupFormData(entry: PendingRecord): FormData {
  const formData = new FormData();
  formData.append("image", entry.annotatedBlob, "capture.jpg");
  formData.append(
    "original",
    entry.originalBlob,
    uploadImageFilename(entry.originalFileName, entry.originalBlob.type),
  );
  formData.append(
    "annotations",
    JSON.stringify({
      drawings: entry.annotations.drawings,
      crop: entry.annotations.crop,
      rotation: entry.annotations.rotation,
      downscaleMaxPx: entry.annotations.downscale_max_px,
    }),
  );
  return formData;
}

/**
 * Assembles the multipart body for a queued APPEND — one photo added to an
 * EXISTING, synced record via `POST /projects/2.0/projects/{id}/records/`.
 *
 * Self-contained mirror of the online append in `use-append-photo.ts`
 * (`buildAppendFormData`): the same `image` / `original` / `taken_at_hint` /
 * `annotations` fields, serialized identically (annotations only when there is
 * markup), so the queued upload is byte-for-byte the request the online path
 * sends.
 *
 * Two differences from the v2.0 create (`buildMultiPhotoFormData`), both deliberate:
 *  - It targets an existing record by its server id (`record_id`) and carries
 *    the photo's own idempotency key (`client_photo_uuid`) so a retry can
 *    never attach the photo twice — there is no `client_record_uuid` because
 *    no new record is being created.
 *  - It is ADD-ONLY: it never sends `plan` / `pin_x` / `pin_y` / `tags` /
 *    `metadata`. An append must not mutate the record's metadata (see
 *    [[append-photo-outbox]]).
 */
export function buildAppendFormData(entry: PendingAppend): FormData {
  const formData = new FormData();

  formData.append("record_id", entry.recordId);
  // The photo's stable idempotency key — also sent as the
  // `X-Idempotency-Key` header by the drain, mirroring `use-append-photo.ts`.
  formData.append("client_photo_uuid", entry.photoId);

  formData.append("image", entry.annotatedBlob, "capture.jpg");
  formData.append(
    "original",
    entry.originalBlob,
    uploadImageFilename(null, entry.originalBlob.type),
  );

  if (entry.takenAtHint) {
    formData.append("taken_at_hint", entry.takenAtHint);
    formData.append("taken_at_source_hint", "file_last_modified");
  }

  const { annotations } = entry;
  const hasDrawings =
    Array.isArray(annotations.drawings) && annotations.drawings.length > 0;
  const hasCrop = annotations.crop != null;
  const hasDownscale = annotations.downscaleMaxPx != null;
  // A rotation with no drawings/crop is still meaningful: it is what puts the
  // photo back the right way up when the record is re-edited.
  const hasRotation = !!annotations.rotation;
  if (hasDrawings || hasCrop || hasDownscale || hasRotation) {
    formData.append(
      "annotations",
      JSON.stringify({
        drawings: annotations.drawings,
        crop: annotations.crop,
        rotation: annotations.rotation,
        downscaleMaxPx: annotations.downscaleMaxPx,
      }),
    );
  }

  return formData;
}
